import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { PROVINCES, tierById, type PayMethod } from '@/lib/sponsor/catalog';
import { num, quote, usd } from '@/lib/sponsor/pricing';
import { placeOrder, useSponsorStore } from '@/lib/sponsor/store';
import Field, { inputClass } from '../components/Field';
import { useSponsorContext } from '../context';

const METHODS: { id: PayMethod; name: string; sub: string }[] = [
  { id: 'khqr', name: 'KHQR', sub: 'Scan and pay now' },
  { id: 'bank', name: 'Bank transfer', sub: 'Invoice, pay in 7 days' },
  { id: 'card', name: 'Card', sub: 'Visa, Mastercard' },
];

function FakeQr() {
  // Decorative stand-in for the KHQR code the payment provider returns.
  const cells: React.ReactNode[] = [];
  const s = 7;
  for (let y = 0; y < 18; y++)
    for (let x = 0; x < 18; x++) {
      const finder = (x < 5 && y < 5) || (x > 12 && y < 5) || (x < 5 && y > 12);
      if (!finder && (x * 31 + y * 17 + x * y * 7) % 5 < 2) cells.push(<rect key={`${x}-${y}`} x={x * s} y={y * s} width={s} height={s} />);
    }
  const fp = (x: number, y: number) => (
    <g key={`f${x}${y}`}>
      <rect x={x} y={y} width={35} height={35} />
      <rect x={x + 5} y={y + 5} width={25} height={25} fill="#fff" />
      <rect x={x + 10} y={y + 10} width={15} height={15} />
    </g>
  );
  return (
    <svg viewBox="0 0 126 126" className="w-28 h-28 bg-white rounded-lg p-1.5 shrink-0" role="img" aria-label="KHQR code (example)">
      <g fill="#000">{cells}{fp(0, 0)}{fp(91, 0)}{fp(0, 91)}</g>
    </svg>
  );
}

export default function SponsorCheckoutPage() {
  const store = useSponsorStore();
  const { selection } = useSponsorContext();
  const navigate = useNavigate();
  const account = store.accounts.find((a) => a.id === store.currentAccountId);

  const [method, setMethod] = useState<PayMethod>('khqr');
  const [company, setCompany] = useState(account?.company ?? '');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState(account?.phone ?? '');
  const [banner, setBanner] = useState('');
  const [link, setLink] = useState('');
  const [provinces, setProvinces] = useState<string[]>(['All provinces']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  if (!account) return <Navigate to="/sponsor/register" replace />;

  const tier = tierById(selection.tier);
  const stop = selection.stops[tier.id];
  const q = quote(tier, stop, selection.periodMonths, selection.segment);

  const toggleProv = (p: string) =>
    setProvinces((cur) => {
      if (p === 'All provinces') return ['All provinces'];
      const rest = cur.filter((x) => x !== 'All provinces');
      const next = rest.includes(p) ? rest.filter((x) => x !== p) : [...rest, p];
      return next.length ? next : ['All provinces'];
    });

  const onSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const e: Record<string, string> = {};
    if (company.trim().length < 2) e.company = 'Enter your company name.';
    if (contact.trim().length < 2) e.contact = 'Enter who we should call.';
    if (phone.replace(/\D/g, '').length < 8) e.phone = 'Enter a phone number with at least 8 digits.';
    if (!banner) e.banner = 'Add your banner image (1200 × 400).';
    if (link && !/^https?:\/\/\S+\.\S+/.test(link.trim())) e.link = 'Enter a link that starts with https://';
    setErrors(e);
    if (Object.keys(e).length) {
      document.getElementById(`co-${Object.keys(e)[0]}`)?.focus();
      return;
    }
    setBusy(true);
    // Simulated provider round-trip. Real KHQR/card calls go through the Loyalty API.
    window.setTimeout(() => {
      placeOrder({
        tier: tier.id, stopIndex: stop, periodMonths: selection.periodMonths, segment: selection.segment, method,
        company: company.trim(), contact: contact.trim(), phone: phone.trim(), link: link.trim(),
        provinces: tier.id === 'starter' ? ['All provinces'] : provinces, bannerName: banner,
      });
      navigate('/sponsor/done');
    }, 700);
  };

  const err = (k: string) => ({ 'aria-invalid': !!errors[k], 'aria-describedby': errors[k] ? `co-${k}-err` : undefined });

  return (
    <form noValidate onSubmit={onSubmit} className="grid gap-5 lg:grid-cols-[1.5fr_1fr] items-start">
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6 flex flex-col gap-4">
        <button type="button" onClick={() => navigate('/sponsor')} className="self-start text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer">‹ Back to packages</button>

        <h2 className="font-heading font-bold">Your company</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="co-company" label="Company name" error={errors.company}>
            <input id="co-company" className={inputClass} value={company} onChange={(e) => setCompany(e.target.value)} {...err('company')} />
          </Field>
          <Field id="co-contact" label="Contact person" error={errors.contact}>
            <input id="co-contact" className={inputClass} value={contact} onChange={(e) => setContact(e.target.value)} {...err('contact')} />
          </Field>
          <Field id="co-phone" label="Phone" error={errors.phone}>
            <input id="co-phone" type="tel" className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} {...err('phone')} />
          </Field>
        </div>

        <h2 className="font-heading font-bold mt-2">Your ad</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="co-banner" label="Banner image (1200 × 400)" error={errors.banner}>
            <input id="co-banner" type="file" accept="image/*" className={`${inputClass} py-2`} onChange={(e) => setBanner(e.target.files?.[0]?.name ?? '')} {...err('banner')} />
          </Field>
          <Field id="co-link" label="Link when users tap" error={errors.link}>
            <input id="co-link" type="url" placeholder="https://" className={inputClass} value={link} onChange={(e) => setLink(e.target.value)} {...err('link')} />
          </Field>
        </div>
        {tier.id !== 'starter' && (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm text-foreground-600 mb-1">Show in provinces</legend>
            <div className="flex flex-wrap gap-2">
              {PROVINCES.map((p) => (
                <label key={p} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm cursor-pointer border ${provinces.includes(p) ? 'bg-background-50 border-foreground-950 font-semibold' : 'bg-background-100 border-transparent'}`}>
                  <input type="checkbox" className="accent-primary-500" checked={provinces.includes(p)} onChange={() => toggleProv(p)} />
                  {p}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <h2 className="font-heading font-bold mt-2">Pay with</h2>
        <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Payment method">
          {METHODS.map((m) => (
            <label key={m.id} className={`rounded-xl border p-3 flex flex-col gap-0.5 cursor-pointer text-xs text-foreground-500 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-400 ${method === m.id ? 'border-2 border-foreground-950 bg-background-100' : 'border-background-300'}`}>
              <input type="radio" name="meth" value={m.id} checked={method === m.id} onChange={() => setMethod(m.id)} className="sr-only" />
              <b className="text-sm text-foreground-950">{m.name}</b>
              {m.sub}
            </label>
          ))}
        </div>
        <div className="flex gap-4 items-center flex-wrap rounded-xl bg-background-100 p-4 text-sm text-foreground-600">
          {method === 'khqr' && (<><FakeQr /><p className="max-w-xs">Scan with any KHQR bank app (ABA, ACLEDA, Wing). The page moves on when the payment arrives. In this demo, press Pay.</p></>)}
          {method === 'bank' && <p>We email an invoice to your contact. Pay by bank transfer within 7 days. Your ads go live after OPS sees the money, usually 1 to 2 working days.</p>}
          {method === 'card' && <p>Visa or Mastercard. You enter the card on your bank's secure page after you press Pay.</p>}
        </div>
      </div>

      <aside className="bg-background-50 border border-background-200 rounded-2xl p-5 flex flex-col gap-3 lg:sticky lg:top-24">
        <h2 className="font-heading font-bold">Order summary</h2>
        <dl className="flex flex-col gap-2 text-sm">
          {[
            ['Package', `${tier.name}${selection.segment === 'ngo' ? ' · NGO' : ''}`],
            ['Period', `${q.months} month${q.months > 1 ? 's' : ''}`],
            ['Views', `${num(q.viewsPerMonth)} / month`],
            ['Apps', tier.apps.length > 2 ? 'All connected' : tier.apps.join(' + ')],
            ['List price', usd(q.list)],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3"><dt className="text-foreground-500">{k}</dt><dd>{v}</dd></div>
          ))}
          {q.list > q.paid && (
            <div className="flex justify-between gap-3"><dt className="text-foreground-500">Offer</dt><dd className="text-primary-600">−{usd(q.list - q.paid)}</dd></div>
          )}
          <div className="flex justify-between gap-3"><dt className="text-foreground-500">VAT 10%</dt><dd>{usd(q.vat)}</dd></div>
        </dl>
        <div className="flex justify-between items-baseline border-t border-dashed border-background-300 pt-3">
          <span>Total</span>
          <b className="font-heading text-3xl">{usd(q.total)}</b>
        </div>
        <button type="submit" disabled={busy} className="w-full py-3.5 rounded-full bg-foreground-950 text-background-50 font-bold cursor-pointer disabled:opacity-60 disabled:cursor-wait">
          {busy ? 'Processing…' : method === 'bank' ? 'Get invoice' : `Pay ${usd(q.total)}`}
        </button>
        <p className="text-xs text-center text-foreground-500">Your ads go live after OPS checks them, within 1 working day. No auto-renew.</p>
      </aside>
    </form>
  );
}
